import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { BigButton } from "../design/BigButton";

/**
 * DebugPreviews — grid of every level's hitbox debug preview PNG.
 *
 * Each thumbnail links to the full-size preview on a new tab.
 * Admin can "Regenerate all" (rebuilds every PNG) or per-level.
 */
export function DebugPreviews() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/debug-previews");
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function regenerateAll() {
    setBusy(true); setMsg(null); setErr(null);
    try {
      const { data } = await api.post("/admin/debug-previews/regenerate-all");
      setMsg(`Regenerated ${data.regenerated} previews.`);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  async function regenerateOne(levelId) {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await api.post(`/admin/levels/${levelId}/debug-preview`);
      setMsg("Preview regenerated.");
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  // Group items by world for readability.
  const byWorld = items.reduce((acc, it) => {
    const key = it.worldName || "Unassigned";
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});

  if (loading) return <p>Loading previews…</p>;

  return (
    <div data-testid="admin-debug-previews">
      <div className="admin-header">
        <div>
          <h1>Hitbox debug previews</h1>
          <p className="admin-header__sub">
            {items.length} levels · thumbnails show the exact polygon each object
            uses for hit-testing (padding is intentionally ignored). Regenerated
            automatically on every level Save.
          </p>
        </div>
        <div className="admin-actions">
          <BigButton onClick={regenerateAll} disabled={busy} data-testid="dbg-regen-all">
            {busy ? "Regenerating…" : "Regenerate all"}
          </BigButton>
        </div>
      </div>

      {err && <div className="admin-error" data-testid="admin-error">{err}</div>}
      {msg && <div className="admin-ok" data-testid="admin-ok">{msg}</div>}

      {Object.entries(byWorld).map(([world, rows]) => (
        <section key={world} style={{ marginBottom: "1.75rem" }}>
          <h2>{world}</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "1rem",
            }}
          >
            {rows.map((it) => {
              const src = `${process.env.REACT_APP_BACKEND_URL}${it.url}?t=${Date.now()}`;
              return (
                <div
                  key={it.levelId}
                  data-testid={`dbg-card-${it.levelSlug}`}
                  style={{
                    border: "2px solid var(--wmleye-line)",
                    borderRadius: 12,
                    padding: "0.75rem",
                    background: "var(--wmleye-surface)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "baseline" }}>
                    <strong>{it.levelName}</strong>
                    <small style={{ color: "var(--wmleye-ink-soft)" }}>
                      {it.objectCount} hitboxes{it.isPublished ? " · published" : ""}
                    </small>
                  </div>
                  {it.exists ? (
                    <a
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`dbg-thumb-${it.levelSlug}`}
                    >
                      <img
                        src={src}
                        alt={`Hitbox preview for ${it.levelName}`}
                        style={{
                          width: "100%",
                          borderRadius: 8,
                          marginTop: "0.5rem",
                          background: "#000",
                          display: "block",
                        }}
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <div
                      style={{
                        padding: "1.5rem",
                        marginTop: "0.5rem",
                        textAlign: "center",
                        background: "var(--wmleye-line)",
                        borderRadius: 8,
                        color: "var(--wmleye-ink-soft)",
                      }}
                      data-testid={`dbg-missing-${it.levelSlug}`}
                    >
                      Not yet generated
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                    <code style={{ flex: 1, fontSize: "0.75rem", color: "var(--wmleye-ink-soft)", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {it.url}
                    </code>
                    <BigButton
                      variant="ghost"
                      onClick={() => regenerateOne(it.levelId)}
                      disabled={busy}
                      data-testid={`dbg-regen-${it.levelSlug}`}
                    >
                      Regenerate
                    </BigButton>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export default DebugPreviews;
